import { formatPhoneNumber, toTelHref } from '../utils/phoneNumber';
import { formatWebsiteLabel, normalizeWebsiteUrl } from '../utils/websiteUrl';
import { formatTimeZoneLabel, isTimeZoneValue } from '../utils/timeZone';
import { normalizeColor } from '../utils/color';

// Single place where the raw `/school/public-profile/{publicPath}` payload is
// converted into the shape the UI renders. Components never read the raw
// response, so a field rename in the API only changes this file.

const MEDIA_TYPES = { IMAGE: 'image', VIDEO: 'video' };

const cleanText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const cleanUrl = (value) => {
  const text = cleanText(value);
  if (!text) return null;

  // Reject placeholder values and anything that is not a usable media source.
  if (!/^(https?:\/\/|\/\/|\/|data:image\/)/i.test(text)) return null;

  return text;
};

/** Split a description into paragraphs while preserving single line breaks. */
const toParagraphs = (value) => {
  const text = cleanText(value);
  if (!text) return [];

  return text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const normalizeMediaType = (value) => {
  const type = cleanText(value)?.toLowerCase();
  if (type === MEDIA_TYPES.VIDEO) return MEDIA_TYPES.VIDEO;
  return MEDIA_TYPES.IMAGE;
};

const mapMediaItem = (rawItem, index, schoolName) => {
  if (!rawItem || typeof rawItem !== 'object') return null;

  const mediaUrl = cleanUrl(rawItem.mediaUrl);
  const thumbnailUrl = cleanUrl(rawItem.thumbnailUrl);
  if (!mediaUrl && !thumbnailUrl) return null;

  const mediaType = normalizeMediaType(rawItem.mediaType);
  const displayOrder = Number.isFinite(Number(rawItem.displayOrder))
    ? Number(rawItem.displayOrder)
    : index;

  const label = schoolName || 'the school';

  return {
    id: `media-${index}`,
    mediaType,
    mediaUrl: mediaUrl || thumbnailUrl,
    thumbnailUrl: thumbnailUrl || (mediaType === MEDIA_TYPES.IMAGE ? mediaUrl : null),
    displayOrder,
    isVideo: mediaType === MEDIA_TYPES.VIDEO,
    alt:
      mediaType === MEDIA_TYPES.VIDEO
        ? `Video ${index + 1} from ${label}`
        : `Photo ${index + 1} of ${label}`,
  };
};

const mapMedia = (rawMedia, schoolName) => {
  if (!Array.isArray(rawMedia)) return [];

  return rawMedia
    .map((item, index) => mapMediaItem(item, index, schoolName))
    .filter(Boolean)
    .sort((a, b) => a.displayOrder - b.displayOrder);
};

const mapPhone = (rawPhone) => {
  const href = toTelHref(rawPhone);
  const display = formatPhoneNumber(rawPhone);
  if (!href || !display) return null;

  return { display, href };
};

const mapWebsite = (rawWebsite) => {
  const href = normalizeWebsiteUrl(rawWebsite);
  const label = formatWebsiteLabel(rawWebsite);
  if (!href || !label) return null;

  return { href, label };
};

/**
 * `location` currently holds an IANA time zone. Present it as a time zone when
 * that is what it is, and only treat it as a place when it reads like one.
 */
const mapLocation = (rawLocation) => {
  const value = cleanText(rawLocation);
  if (!value) return { timeZone: null, timeZoneLabel: null, place: null };

  if (isTimeZoneValue(value)) {
    return {
      timeZone: value,
      timeZoneLabel: formatTimeZoneLabel(value),
      place: null,
    };
  }

  return { timeZone: null, timeZoneLabel: null, place: value };
};

/**
 * Convert the API payload's `result` object into the school profile view model.
 * Missing or unusable fields become null / empty arrays so the UI can simply
 * check for presence.
 */
export const mapSchoolProfile = (rawResult, publicPath = null) => {
  const source = rawResult && typeof rawResult === 'object' ? rawResult : {};

  const schoolName = cleanText(source.schoolName) || 'This School';
  const media = mapMedia(source.media, cleanText(source.schoolName));
  const logoUrl = cleanUrl(source.schoolLogoImageUrl);
  const descriptionParagraphs = toParagraphs(source.publicDescription);
  const { timeZone, timeZoneLabel, place } = mapLocation(source.location);

  const images = media.filter((item) => !item.isVideo);
  const featuredImage = images[0] || null;

  return {
    publicPath,
    schoolName,
    logoUrl,
    // Null when missing / invalid: the theme falls back to a default accent.
    themeColor: normalizeColor(source.themeColor),
    // The school's own academic calendar. Applications are filed against these
    // rather than anything the browser works out for itself.
    academic: {
      currentYear: Number.isFinite(Number(source.currentYear)) ? Number(source.currentYear) : 0,
      currentQuarter: Number.isFinite(Number(source.currentQuarter))
        ? Number(source.currentQuarter)
        : 0,
      useAcademicYear: Boolean(source.useAcademicYear),
    },
    address: cleanText(source.address),
    phone: mapPhone(source.phoneNumber),
    website: mapWebsite(source.website),
    timeZone,
    timeZoneLabel,
    place,
    descriptionParagraphs,
    hasDescription: descriptionParagraphs.length > 0,
    media,
    hasMedia: media.length > 0,
    featuredImageUrl: featuredImage ? featuredImage.mediaUrl : null,
    // Preferred image for link previews: logo first, then a school photo.
    shareImageUrl: logoUrl || (featuredImage ? featuredImage.mediaUrl : null),
  };
};

export { MEDIA_TYPES };
